import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { historyIndex } from './navTransition';

/**
 * BACK RETRACES THE PERSON'S STEPS (SH-A8, owner-48: "wenn ich den zurueck knopf druecke
 * lande ich bei People statt auf der seite wo ich war. Das ist falsch der Backbutton
 * sollte mich immer zu meinem vorherigen ort bringen oder nicht und nicht hardcoded
 * irgendwohin?").
 *
 * APPLE'S RULE. Back pops the stack, it does not route to a stated parent: "People can add
 * views to the top of the stack ... and remove views using built-in, platform-appropriate
 * controls, like a Back button", and for an arrival from outside the answer is to SEED the
 * same stack rather than to invent a parent - "Use a path to support deep links, state
 * restoration, or other kinds of programmatic navigation"
 * (https://developer.apple.com/documentation/swiftui/navigationstack;
 * https://developer.apple.com/design/human-interface-guidelines/toolbars, Navigation).
 *
 * THREE ANSWERS, IN THIS ORDER, and each one is a different fact about how this screen was
 * reached.
 *
 * 1. THIS APP PUSHED. `createBrowserHistory` writes `{usr, key, idx}` into `history.state`
 *    and `idx > 0` means at least one push happened inside the app, so there is a previous
 *    screen and it is ours. A real `history.back()` also fires `popstate`, which is what
 *    animates the pop and what the browser's own back button does.
 *
 * 2. THE TAB REMEMBERS WHERE THE PERSON WAS. A deep link loads a new DOCUMENT, and that
 *    resets the index to 0 even when the person was standing in this app a second earlier
 *    (measured: `idx` is 0 after any full page load, because react-router seeds it when
 *    `history.state` carries none). The tab still knows: the shell writes the last APP path
 *    into `sessionStorage` on every navigation, sessionStorage survives a document load and
 *    dies with the tab, so it answers exactly the question "was this person somewhere in
 *    this app in this tab" - which is the seeded path Apple describes, without ever
 *    stepping back into whatever site sent them here.
 *
 * 3. NOTHING BEHIND US. A pasted link in a new tab, a bookmark, a fresh window: then the
 *    stated parent is the only sensible answer, and it is the caller's, because only the
 *    screen knows which pane it belongs to.
 */

/** Where the shell parks the last app path, so a fresh document can still read it. */
export const LAST_APP_PATH_KEY = 'ofmos.nav.lastAppPath';

/** Read at CLICK TIME rather than at render: it is a property of the stack at that moment. */
function pushedInsideTheApp(): boolean {
  const index = historyIndex(window.history.state);
  return index !== null && index > 0;
}

/**
 * Storage can throw (private mode, a blocked third-party context), and a back button that
 * throws is worse than one that falls through to the stated parent.
 */
export function rememberedAppPath(): string | null {
  try {
    const value = window.sessionStorage.getItem(LAST_APP_PATH_KEY);
    return value !== null && value.startsWith('/') ? value : null;
  } catch {
    return null;
  }
}

export function rememberAppPath(path: string): void {
  try {
    window.sessionStorage.setItem(LAST_APP_PATH_KEY, path);
  } catch {
    /* A tab that cannot remember falls back to the stated parent, which is rule 3. */
  }
}

export interface BackTarget {
  /** Runs the pop. Returns false when it did nothing, so a link can navigate instead. */
  readonly goBack: () => boolean;
}

export function useBack(fallback?: string | undefined): BackTarget {
  const navigate = useNavigate();
  const goBack = useCallback(() => {
    if (pushedInsideTheApp()) {
      void navigate(-1);
      return true;
    }
    /**
     * THE TWO SEEDED ANSWERS REPLACE, THEY DO NOT PUSH (round 3). Back REMOVES the topmost
     * item - "The system disables backward navigation controls when the stack is empty and
     * the root view ... is visible" - so a back that pushed its answer would leave a forward
     * entry pointing at the screen the person just left, and the next press of the same
     * control would walk into it instead of one step further out. Measured on the settings
     * ladder: from a deep-linked pane detail, a pushing fallback made back-back-back read
     * detail, list, detail. A replace keeps the index where a document load put it, so every
     * press answers the same question one level higher up.
     */
    const remembered = rememberedAppPath();
    if (remembered !== null && remembered !== window.location.pathname + window.location.search) {
      void navigate(remembered, { replace: true });
      return true;
    }
    if (fallback !== undefined) {
      void navigate(fallback, { replace: true });
      return true;
    }
    return false;
  }, [fallback, navigate]);
  return { goBack };
}
